// Browser softphone interface (docs/ringcentral/SOFTPHONE.md). The console talks to the phone only through this
// interface; the phone itself (one per browser, shared by every tab) is client.ts → hub.ts → an engine.

import { useEffect, useSyncExternalStore } from 'react';
import { IDLE, type ControlAction, type ControlArgs, type PhoneCall, type PhoneState } from './engine.ts';

export type { ControlAction, ControlArgs, PhoneCall, PhoneState } from './engine.ts';

export interface SoftphoneCall {
  /** The phone's id for the call. */
  callId: string | null;
  /** Server-side telephony session id; the API links it to the call action. */
  telephonySessionId: string | null;
  direction: 'outbound' | 'inbound' | null;
  /** Dialed / calling numbers as the phone reports them. */
  to: string | null;
  from: string | null;
}

export type SoftphoneEvent =
  | { type: 'login'; loggedIn: boolean }
  | { type: 'ringing'; call: SoftphoneCall }
  | { type: 'started'; call: SoftphoneCall }
  | { type: 'ended'; call: SoftphoneCall };

export interface SoftphoneState {
  /** idle: not connected yet; loading: registering or reconnecting; ready: registered (or waiting for sign-in); error: no phone. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** null until the phone knows; false: the operator must sign in (RingCentral). */
  loggedIn: boolean | null;
  /** The call panel is expanded. */
  open: boolean;
  error: string | null;
  phone: PhoneState;
}

export interface Softphone {
  /** Shown in the UI ("RingCentral", "Simulated phone"). */
  readonly label: string;
  readonly engine: 'simulator' | 'ringcentral';
  /** Connects to the phone once; later calls do nothing. */
  load(): void;
  /**
   * Places a call from the phone. The panel opens for it unless `openPanel` is false: a call placed from an event keeps
   * its controls on the event's contact row and leaves the panel shut.
   */
  dial(e164: string, callerId?: string | null, o?: { openPanel?: boolean }): Promise<void>;
  /** Hangs up the call with this telephony session (or the phone's only live call). */
  hangup(telephonySessionId: string | null): Promise<void>;
  /** Any control on a call the phone holds. Rejects with the reason when the provider couldn't do it. */
  control(callId: string, action: ControlAction, args?: ControlArgs): Promise<{ consultCallId?: string }>;
  /** RingCentral: signs this operator in (a pop-up to RingCentral; the server keeps the token). */
  signIn(): Promise<void>;
  signOut(): Promise<void>;
  /** The network is back, or the operator asks: reconnect and reread the calls. */
  reconnect(): void;
  /** The agent's workstation changed: register again as the new phone. */
  restart(): void;
  /** A 'softphone' message from the realtime stream. */
  serverMessage(data: unknown): void;
  /** This tab is in use (it plays the audio and makes the phone's server calls). */
  activate(): void;
  setOpen(open: boolean): void;
  getState(): SoftphoneState;
  /** State changes (for useSyncExternalStore). */
  subscribe(cb: () => void): () => void;
  /** Call and sign-in notifications. */
  on(listener: (e: SoftphoneEvent) => void): () => void;
}

const NO_STATE: SoftphoneState = { status: 'idle', loggedIn: null, open: false, error: null, phone: IDLE };
const noSub = () => () => undefined;

/** The softphone's state, or a fixed idle state when there is none. */
export function useSoftphoneState(sp: Softphone | null): SoftphoneState {
  return useSyncExternalStore(
    sp ? sp.subscribe : noSub,
    sp ? sp.getState : () => NO_STATE,
    sp ? sp.getState : () => NO_STATE,
  );
}

/**
 * Whether an event is open in the workspace (whichever tab is showing). While it is, the phone stays out of its way:
 * calls placed from the event don't open the panel, and a call ringing in shows in the header rather than over the
 * event. With no event open, a ringing call opens the panel as before.
 */
let eventViews = 0;
const eventSubs = new Set<() => void>();
const eventView = {
  subscribe(cb: () => void) {
    eventSubs.add(cb);
    return () => void eventSubs.delete(cb);
  },
  get: () => eventViews > 0,
};

export function useEventInView(): boolean {
  return useSyncExternalStore(eventView.subscribe, eventView.get, eventView.get);
}

/** Marks an event as open while the calling component is mounted (ConsoleScreen, with the event's workspace). */
export function useMarkEventInView(): void {
  useEffect(() => {
    eventViews++;
    eventSubs.forEach((cb) => cb());
    return () => {
      eventViews--;
      eventSubs.forEach((cb) => cb());
    };
  }, []);
}

/** Last 10 digits, for matching numbers written differently (+1 480…, (480) …). */
export function phoneKey(n: string | null | undefined): string {
  return (n ?? '').replace(/\D/g, '').slice(-10);
}

/** The console's view of the phone's state. */
export function softphoneState(phone: PhoneState, open: boolean): SoftphoneState {
  const r = phone.registration;
  const status = r === 'off' ? 'idle' : r === 'starting' || r === 'reconnecting' ? 'loading' : r === 'error' ? 'error' : 'ready';
  return { status, loggedIn: r === 'registered' ? true : r === 'needs_sign_in' ? false : null, open, error: phone.error, phone };
}

/** Call notifications from one state to the next: a new ringing call, a call connected for the first time, a call ended. */
export function callEvents(before: PhoneCall[], after: PhoneCall[]): SoftphoneEvent[] {
  const prev = new Map(before.map((c) => [c.id, c]));
  const out: SoftphoneEvent[] = [];
  const view = (c: PhoneCall): SoftphoneCall => ({
    callId: c.id,
    telephonySessionId: c.telephonySessionId,
    direction: c.direction,
    to: c.direction === 'outbound' ? c.remote : c.line,
    from: c.direction === 'outbound' ? c.line : c.remote,
  });
  for (const c of after) {
    const p = prev.get(c.id);
    if (!p && c.state === 'ringing') out.push({ type: 'ringing', call: view(c) });
    if ((!p || p.state === 'ringing') && (c.state === 'connected' || c.state === 'held')) out.push({ type: 'started', call: view(c) });
    if (p && p.state !== 'ended' && c.state === 'ended') out.push({ type: 'ended', call: view(c) });
  }
  for (const p of before) if (p.state !== 'ended' && !after.some((c) => c.id === p.id)) out.push({ type: 'ended', call: view(p) });
  return out;
}
